import { lazy, Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import type { RootState } from '$store';
import Loader from '~components/generic/loader/Loader';
import PageContainer from '~components/page/PageContainer';
import useAppDispatch from '~hooks/useAppDispatch';
import { useNavigateToDefault } from '~hooks/useNavigateToPage';
import { ControlActions } from '~store/actions';
import {
  selectActiveAnchor,
  selectConfigFallbackForbiddenUrl,
  selectCurrentPageId,
  selectLocationPath,
  selectPageIdByPath,
  selectPageLoadingStateByAlias,
  selectStatusShowEditLoading,
} from '~store/selectors';
import { fetchPage } from '~store/thunks/page';

const ForbiddenPage = lazy(() => import('~pages/ForbiddenPage'));
const NotFoundPage = lazy(() => import('~pages/NotFoundPage'));
const TimeoutPage = lazy(() => import('~pages/TimeoutPage'));

const ContentPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigateToDefault = useNavigateToDefault();

  const [isLoading, setIsLoading] = useState(true);
  const [isPageNotFound, setIsPageNotFound] = useState(false);
  const [isPageForbidden, setIsPageForbidden] = useState(false);
  const [isPageTimedOut, setIsPageTimedOut] = useState(false);

  const pageAlias = useSelector(selectLocationPath);
  const activeAnchor = useSelector(selectActiveAnchor);

  // reset state on alias changed
  useEffect(() => {
    setIsLoading(true);
    setIsPageNotFound(false);
    setIsPageForbidden(false);
    setIsPageTimedOut(false);
  }, [pageAlias]);

  // current page id in not available until the page is saved in store
  const currentPageId = useSelector(selectCurrentPageId);

  const showEditLoading = useSelector(selectStatusShowEditLoading);

  useEffect(() => {
    // fetch page, if not awailabel in store
    if (currentPageId === null) dispatch(fetchPage({ page: pageAlias, pageId: currentPageId }));
  }, [pageAlias, currentPageId, dispatch]);

  // track page view
  useEffect(() => {
    const pageView: PageView = {
      title: pageAlias,
      location: window.location.href,
      pathname: location.pathname,
    };
    dispatch(ControlActions.pageView(pageView));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageAlias, activeAnchor]);

  // handle error states
  const pageLoadingState = useSelector(state => selectPageLoadingStateByAlias(state as RootState, pageAlias));
  useEffect(() => {
    setIsLoading(pageLoadingState?.isLoading || false);
    setIsPageForbidden(!pageLoadingState?.isLoading && !!pageLoadingState?.forbidden);
    setIsPageNotFound(!pageLoadingState?.isLoading && !!pageLoadingState?.notFound);
    setIsPageTimedOut(!pageLoadingState?.isLoading && !!pageLoadingState?.timeout);
    if (pageLoadingState?.serverError) {
      navigateToDefault();
    }
  }, [navigateToDefault, pageLoadingState]);

  // check if custom forbidden page is available
  const fallbackForbiddenUrl = useSelector(selectConfigFallbackForbiddenUrl);
  const fallbackForbiddenId = useSelector(state => selectPageIdByPath(state as RootState, fallbackForbiddenUrl));

  return (
    <Suspense fallback={<Loader absoluteCenter={true} />}>
      {isLoading && <Loader absoluteCenter={true} />}
      {showEditLoading && <Loader absoluteCenter={true} transparentBG={true} />}
      {isPageForbidden && fallbackForbiddenId && <PageContainer entityId={fallbackForbiddenId} />}
      {isPageForbidden && <ForbiddenPage />}
      {isPageNotFound && <NotFoundPage />}
      {isPageTimedOut && <TimeoutPage />}
      {currentPageId && <PageContainer entityId={currentPageId} />}
    </Suspense>
  );
};
export default ContentPage;
